import { z } from 'zod';
import { ulidSchema } from './id';
import { RANK_MAX_LENGTH } from './rank';

/**
 * Labels — the contract layer for block **B15**.
 *
 * `BE-15.1` (`FR-CARD-010`, manage the board's label set: create, rename,
 * recolour and delete) and `BE-15.2` (`FR-CARD-009`, apply a label to a card
 * and take it off again).
 *
 * ---------------------------------------------------------------------------
 * ONE NOUN, TWO REQUIREMENTS, AND THE SPLIT RUNS THROUGH EVERY SHAPE BELOW
 * ---------------------------------------------------------------------------
 * `FR-CARD-010` owns the **set**: a label is board-scoped, it has a name and a
 * colour, and the board's popover is where it is created, renamed, recoloured
 * and deleted. `FR-CARD-009` owns the **application**: a card carries any
 * number of the board's labels, and the picker is a toggle.
 *
 * That is why this file declares two view shapes for one row. `labelSchema` is
 * the set's member — everything the popover manages, including the rank that
 * fixes set order. `cardLabelSchema` is three fields of it, because a card's
 * labels ride on the **board face**, which is the one response in the product
 * that scales with the size of the board. See `cardLabelSchema`'s docstring;
 * the argument is `cardListRefSchema`'s in `./card-detail`, one level down and
 * considerably sharper.
 *
 * ⚠ **This file is imported by two files that shipped before it, and the
 * direction is correct rather than backwards.** `./board-contents` (B13) and
 * `./card-detail` (B14) both take `cardLabelSchema` from here. The *element*
 * declaration belongs to the block that owns labels, and having exactly one
 * `cardLabelSchema` is what keeps the card front and the card back from
 * serving two shapes of one label — the same argument `./board-contents`
 * makes for composing `cardSchema` rather than restating it. There is no
 * cycle: this file imports `./id` and `./rank` and nothing else in the
 * package.
 *
 * ⚠ **And `cardSchema` is NOT widened, which reverses a prediction three
 * shipped docstrings carried.** `./board-contents`, `./card-detail` and
 * `./index` each said a badge block would widen `cardSchema` and the board
 * face would inherit the field for free. B15 is the first badge block and it
 * extended on **copies** instead — `boardFaceCardSchema` in `./board-contents`
 * and `cardDetailSchema` in `./card-detail` — so B12's four card routes gain
 * no `labels` field and need no label read. The three docstrings are corrected
 * in place rather than deleted; read the correction in any of them before
 * choosing for dates (`BE-24.x`) or the cover (`BE-25.x`), because the trade
 * is genuinely two-sided and this brief decided it one way.
 *
 * ---------------------------------------------------------------------------
 * THIS IS NOT `BE-02.1`, AND "label" BEING ONE OF ITS SIX NOUNS IS WHY IT
 * LOOKS LIKE IT
 * ---------------------------------------------------------------------------
 * `BE-02.1` — the full Phase 1 object model (board, list, card, comment, label,
 * notification) — is a different item and stays PENDING. `./list`, `./card`,
 * `./board-contents` and `./card-detail` each recorded that about themselves,
 * and this file is the first whose noun is named in `BE-02.1`'s own list: a
 * reader who greps `BE-02.1` and finds `label.ts` will reasonably conclude a
 * third of the object model has landed. It has not. What is below is what
 * B15's seven routes accept and return, and no more — a `labels` row has no
 * field this file omits, which is a fact about how narrow the table is rather
 * than evidence that the object model arrived.
 *
 * ---------------------------------------------------------------------------
 * ⚠ ONE `FR-CARD-009` CLAUSE HAS NO SHAPE HERE AND NO COLUMN ANYWHERE
 * ---------------------------------------------------------------------------
 * `FR-CARD-009`'s behaviour text: "A **board-level toggle** switches card-front
 * labels between colour-only bars and colour-plus-name", with AC3 and the `;`
 * shortcut resting on it. **Measured against the live catalogue: no column
 * holds it.** `boards` carries nineteen settings — `show_card_numbers_on_front`,
 * `covers_enabled`, `card_front_hover_actions_enabled` and the rest — and
 * nothing about labels; a search for every `%label%`-named column in the
 * database returns twelve, none of them a display flag.
 *
 * So B15 ships **no persistence** for it, and what it does instead is make the
 * toggle renderable: `name` is on the wire for every label on every surface
 * (`labelSchema.name`, `cardLabelSchema.name`), so `FE-15.2` can honour AC3
 * from client-side state. The account- or board-scoped version needs a
 * requirement, a column and an owner, and it has none of the three. **Flagged,
 * not invented** — `implementation-plan.md` §9's rule.
 *
 * ---------------------------------------------------------------------------
 * WHAT THIS FILE DELIBERATELY DOES NOT DECLARE
 * ---------------------------------------------------------------------------
 *   - **A reorder body.** `labels.position` is real and
 *     `idx_labels_board_id_position` indexes it, but `FR-CARD-010` describes no
 *     reorder gesture, so B15 only ever appends and there is no route, no event
 *     name (`0017`'s header says so from the SQL side) and no shape. A block
 *     that builds one adds all three together.
 *   - **A uniqueness rule.** There is no unique index on `(board_id, name)` or
 *     `(board_id, colour)`, and none should be invented: `FR-CARD-010`'s edge
 *     case is "two labels on the same board may share a colour with different
 *     names", nothing forbids two labels sharing a *name*, and a duplicate is
 *     the client dialog's problem rather than the schema's.
 *   - **A per-board cap.** `FR-CARD-009`'s ">10 labels" edge case calls the
 *     per-list suggestion "a convenience, not a constraint".
 *   - **Anything about filtering.** `FR-CARD-012` (filter the board by a label
 *     from the card) and `FR-SEARCH-003` are `B20`'s; `board_filter_labels` and
 *     `board_filter_states.no_labels` are real tables B15 does not touch.
 *   - **A colour-blind mode.** `FR-CARD-011` is built once as `FR-EXP-021`, a
 *     per-user account setting rather than a field on a label.
 *
 * The file follows the division every other feature file here follows —
 * **input primitives**, which validate and normalise what a route accepts, and
 * **views**, which describe what a route returns. The name is where that
 * division does its work here: the input trims and refuses empty, the view is
 * width-bounded and refuses nothing a row can hold.
 */

// ---------------------------------------------------------------------------
// Column widths and the palette.
// ---------------------------------------------------------------------------

/**
 * `labels.name varchar(255)` — and unlike `CARD_DESCRIPTION_MAX_LENGTH` in
 * `./card-detail`, this **is** a column width. Over it is `value too long for
 * type character varying(255)` at INSERT, so losing the bound here is an ugly
 * 500 rather than a cap silently ceasing to exist.
 *
 * Declared rather than imported, on `./card`'s rule: "import the constant that
 * describes YOUR column if one exists, declare it if none does, and never share
 * one across two columns." Nothing in this package describes `labels.name` —
 * `LIST_TITLE_MAX_LENGTH` (512) is `lists.title`, `BIO_MAX_LENGTH` is
 * `users.bio` — so this is the one, and it must not be shared with
 * `custom_field_definitions.name` or any other `varchar(255)` a separate
 * requirement could widen separately.
 *
 * Counted in **UTF-16 code units**, because that is what `z.string().max()`
 * measures and what a browser's `input.value.length` reports.
 */
export const LABEL_NAME_MAX_LENGTH = 255;

/**
 * Every value `labels.colour` accepts — `FR-CARD-010`, "a palette of 30
 * colours plus a colourless option".
 *
 * 10 hues × 3 shades + 1 = **31**, and the order below is
 * `ck_labels_colour`'s own, read off the live constraint rather than
 * reconstructed: `colourless` first, then green, yellow, orange, red, purple,
 * blue, sky, lime, pink, black, each as `<hue>_subtle`, `<hue>`, `<hue>_bold`.
 * Keeping the two lists in the same order is what makes a drift between them
 * visible in a diff rather than only in a failing INSERT.
 *
 * ⚠ **The middle shade carries NO suffix.** There is no `green_normal`, no
 * `green_plain` and no `green_medium` — the plain hue *is* the middle shade.
 * A client that invents one gets a 422 from `labelColourSchema` naming the
 * field, which is the whole reason this is an enum rather than a bounded
 * string: the alternative is a `check_violation` from Postgres at INSERT time,
 * surfacing as a 500 on a request the caller could have been told about.
 *
 * ⚠ **`colourless` is a member of the palette, not the absence of one.**
 * `labels.colour` is `NOT NULL`, so "this label has no colour" has exactly one
 * spelling and it is this string. `FR-CARD-009` gives it a rendering rule —
 * "colourless labels render only on the card back" — and that rule is
 * **`FE-15.2`'s alone**: no read in this block filters on it. Filtering
 * server-side would make `GET /boards/:id/contents` and `GET /cards/:id`
 * return different `labels` arrays for one card with no status code to say so,
 * and would break `FR-CARD-012`/`FR-SEARCH-003` by construction, since "filter
 * by this label" would return zero cards for a label the card demonstrably
 * carries.
 */
export const LABEL_COLOURS = [
  'colourless',
  'green_subtle',
  'green',
  'green_bold',
  'yellow_subtle',
  'yellow',
  'yellow_bold',
  'orange_subtle',
  'orange',
  'orange_bold',
  'red_subtle',
  'red',
  'red_bold',
  'purple_subtle',
  'purple',
  'purple_bold',
  'blue_subtle',
  'blue',
  'blue_bold',
  'sky_subtle',
  'sky',
  'sky_bold',
  'lime_subtle',
  'lime',
  'lime_bold',
  'pink_subtle',
  'pink',
  'pink_bold',
  'black_subtle',
  'black',
  'black_bold',
] as const;

/**
 * The six colours `POST /boards` seeds a new board's label set with, in set
 * order — `FR-CARD-010`, "A new board starts with a small set of **unnamed**
 * coloured labels ready to use."
 *
 * The six plain hues, which is Trello's own starting set and the reading that
 * makes "a small set" concrete without inventing a number the requirement
 * withholds. All six are created with `name: null` — an unnamed label is the
 * **absence** of a name, not an empty string, and `labelNameSchema` below
 * makes that the only spelling on the wire too.
 *
 * ⚠ **The seed emits no `label.created`.** It is part of what `board.created`
 * already describes, exactly as the creator's admin `board_members` row and the
 * `workspace_visible` default are, and neither of those emits an event of its
 * own. Six entries at the head of every board's feed would attribute to the
 * creator a vocabulary they did not choose, for ever, on an append-only table
 * with no delete path.
 *
 * ⚠ **Boards created before B15 are NOT backfilled**, and no route may assume
 * a non-empty set. `GET /boards/:id/labels` answers `[]`,
 * `boardContentsSchema.labels` is `[]`, `cardDetailSchema.labels` is `[]`, and
 * `POST /boards/:id/labels` takes `rankBetween(null, null)`'s genuine
 * empty-set path. A board that predates B15 and a board whose six labels were
 * all deleted are the **same state** and nothing distinguishes them — which is
 * why a backfill migration would be adding rows it cannot tell from ones a
 * member deliberately removed.
 *
 * Typed against `LabelColour` so the seed cannot name a colour
 * `ck_labels_colour` refuses, and declared here rather than in the server
 * because `apps/server/test/unit/FR-CARD-010.spec.ts` asserts it.
 */
export const DEFAULT_BOARD_LABEL_COLOURS: readonly LabelColour[] = [
  'green',
  'yellow',
  'orange',
  'red',
  'purple',
  'blue',
] as const;

// ---------------------------------------------------------------------------
// INPUT primitives — what a route ACCEPTS.
// ---------------------------------------------------------------------------

/**
 * A colour as `labels.colour` stores it, on the way in.
 *
 * `z.enum` over the palette, so an unknown value is a **422 naming the field**
 * rather than a `check_violation` surfacing as a 500. That is
 * `boardVisibilitySchema`'s call for the same class of column (a CHECK-
 * constrained `varchar`), and the only thing keeping this enum honest against
 * the constraint is the integration assertion that reads
 * `ck_labels_colour`'s live member list and compares it to `LABEL_COLOURS`.
 */
export const labelColourSchema = z.enum(LABEL_COLOURS);

export type LabelColour = z.infer<typeof labelColourSchema>;

/**
 * A label name as `labels.name` stores it — trimmed, non-empty, at most 255.
 *
 * `.trim().min(1)` is `listTitleSchema`'s and `bioSchema`'s idiom, reused
 * rather than re-decided, and here the trim is what makes the **nullability**
 * below expressible rather than a house preference. Because the primitive
 * trims, the boundary can tell `''` from `'   '` and refuse *both*, which
 * leaves `null` as the single spelling of "unnamed" on the wire and `NULL` as
 * the single spelling in the column.
 *
 * ⚠ **`''` IS A 422, NOT A SILENT `NULL`, AND THAT IS THE OPPOSITE OF
 * `cardDescriptionPatchSchema`.** `./card-detail`'s description accepts `''`
 * and lets the *route* collapse it to `NULL`, because a description is
 * Markdown and must not be trimmed — four leading spaces are an indented code
 * block, two trailing spaces are a hard line break — so its boundary genuinely
 * cannot tell `''` from `'   '` without rewriting content. None of that is true
 * of a label name. The closer sibling is `bioPatchSchema`, which takes
 * `bioSchema.nullable()` and rejects `''` under the rule "clearing a bio is
 * `null`, never `''` — one state, one representation". Both files want the same
 * invariant in the column and enforce it at the layer their primitive allows.
 *
 * The consequence a client has to know: **a text field that has been emptied
 * sends `{ name: null }`, not `{ name: '' }`.** A 422 on the second is the
 * honest answer, because accepting both would give the caller two spellings of
 * one intention — the third option `cardDescriptionPatchSchema` rejects for the
 * description and `bioSchema` rejects for the bio.
 *
 * Note what the trim does **not** license: a long name is stored and returned
 * whole up to the column width. Any ellipsis on a card front is `FE-15.2`'s
 * rendering, and truncating here would destroy the text the hover is supposed
 * to reveal — `listTitleSchema`'s note, one column over.
 */
export const labelNameSchema = z.string().trim().min(1).max(LABEL_NAME_MAX_LENGTH);

/**
 * `POST /boards/:id/labels` — `FR-CARD-010` AC1, "creates a label with a name
 * and colour".
 *
 * ⚠ **No `boardId`.** The board is the path segment, so a body `boardId`
 * beside a path `:id` would be two spellings of one fact that can disagree,
 * and the route would have to decide which wins. This is a deliberate
 * divergence from `createListBodySchema` in `./list`, whose body *does* carry
 * `boardId` — `POST /lists` has no path segment to disagree with.
 *
 * ⚠ **No neighbours and no `position`.** `createListBodySchema` and
 * `createCardBodySchema` both take `previous*Id`/`next*Id` because
 * `FR-BOARD-009` AC3 and `FR-BOARD-020` AC2 specify insert-into-a-gap
 * gestures. `FR-CARD-010` specifies none, so a new label is always **appended**
 * to the board's set and neighbours here would be inventing an interaction. A
 * `position` key is rejected rather than ignored, because no client may ever
 * compute a rank (SC-2) — that is the server's, inside the transaction.
 *
 * `name` defaults to `null` because `FR-CARD-010`'s popover creates unnamed
 * labels and an omitted key is the natural spelling of "I did not name it".
 * The type below is `z.infer` — the **output** — which is what a handler
 * receives after parsing, matching `CreateListBody` and `CreateCardBody`, both
 * of which derive the same way over `.default(null)` fields.
 */
export const createLabelBodySchema = z.strictObject({
  name: labelNameSchema
    .nullable()
    .default(null)
    .describe(
      'The label name, or null for an unnamed label. Trimmed; an empty or ' +
        'whitespace-only string is refused (422) rather than stored as null, so ' +
        '“unnamed” has exactly one spelling. Omit the key entirely for the ' +
        'unnamed labels FR-CARD-010’s popover creates.',
    ),
  colour: labelColourSchema.describe(
    'One of the 31 palette members (30 colours plus “colourless”). Required: ' +
      'labels.colour is NOT NULL and there is no default, because a label with ' +
      'no colour is spelt “colourless” rather than omitted.',
  ),
});

export type CreateLabelBody = z.infer<typeof createLabelBodySchema>;

/**
 * `PATCH /labels/:id/name` — `FR-CARD-010`. SC-4, field-scoped.
 *
 * The name moves on its own and can move nothing else — **never a
 * `PUT /labels/:id`** carrying name and colour together. `boardTitlePatchSchema`
 * gives the argument in full; the specific cost here is that a combined body
 * could not distinguish "rename it, leave the colour" from "rename it and set
 * the colour to whatever I happened to be holding", and the two events this
 * block registers (`label.renamed`, `label.recoloured`) exist precisely because
 * the feed must render those as different sentences.
 *
 * ⚠ **`null` is legal and clears the name.** `FR-CARD-010`'s popover creates
 * unnamed labels, so unnamed has to be reachable by edit too — otherwise a
 * label could be named once and never un-named, and the six seeded labels
 * would be in a state no route could return to. `''` and `'   '` are 422s; see
 * `labelNameSchema`.
 */
export const labelNamePatchSchema = z.strictObject({
  name: labelNameSchema
    .nullable()
    .describe(
      'The new label name, or null to clear it back to unnamed. Trimmed; an ' +
        'empty or whitespace-only string is refused (422). Required — there is ' +
        'no default here, because a rename with no name in the body is a request ' +
        'that says nothing.',
    ),
});

export type LabelNamePatch = z.infer<typeof labelNamePatchSchema>;

/**
 * `PATCH /labels/:id/colour` — `FR-CARD-010`. SC-4, field-scoped.
 *
 * ⚠ **`colour` is NOT nullable, and that asymmetry with
 * `labelNamePatchSchema` is deliberate.** `labels.colour` is `NOT NULL` and the
 * palette already contains an explicit member meaning "no colour"
 * (`'colourless'`), so a nullable field would be a *second* spelling of a state
 * the vocabulary has one word for. `labels.name` is nullable and has no such
 * word, which is why the sibling above accepts `null`. One state, one
 * representation — the rule `./user` states for the bio, applied to two columns
 * that answer it differently.
 */
export const labelColourPatchSchema = z.strictObject({
  colour: labelColourSchema.describe(
    'The new colour, one of the 31 palette members. Not nullable: “no colour” ' +
      'is spelt “colourless”, which is a member of the palette rather than the ' +
      'absence of one.',
  ),
});

export type LabelColourPatch = z.infer<typeof labelColourPatchSchema>;

/**
 * `POST /cards/:id/labels` — `FR-CARD-009` AC1, the picker's checkbox.
 *
 * One field, and it is a **label id** rather than a name or a colour:
 * `FR-CARD-010` makes labels board-scoped and AC4 makes the scoping
 * observable ("open another board → the first board's labels are not present in
 * the second board's label set"), so every candidate is a row the client has
 * already read from the board's set. A name would be ambiguous by construction,
 * since nothing forbids two labels on one board sharing one — see the header.
 *
 * ⚠ **Re-applying a label the card already carries is a legal, idempotent
 * no-op that writes NO activity entry.** `FR-CARD-009` AC1's checkbox is a
 * toggle two clients can fire at once, and the route decides it with
 * `INSERT … ON CONFLICT DO NOTHING RETURNING` rather than SELECT-then-write,
 * whose losing side is a 500 on `card_labels_pkey` for an operation the
 * requirement calls a no-op. B14's members pair records the same rule; this
 * schema carries no flag for it, because both outcomes are 200s with the same
 * body and a status code that distinguished them would be leaking which of two
 * concurrent clicks landed first.
 *
 * ⚠ **There is no `boardId` and no same-board flag.** The card names its board
 * and the server checks the label belongs to it; a caller cannot assert that
 * fact and cannot be trusted to. A label from another board gets the **same
 * 404** a fabricated ULID gets, so the picker is not an existence oracle over
 * label ids.
 */
export const applyCardLabelBodySchema = z.strictObject({
  labelId: ulidSchema.describe(
    'A label from THIS card’s board (FR-CARD-010, AC4). A label id from another ' +
      'board is refused with the same 404 a nonexistent one gets. Applying a ' +
      'label the card already carries is a legal no-op and records nothing.',
  ),
});

export type ApplyCardLabelBody = z.infer<typeof applyCardLabelBodySchema>;

// ---------------------------------------------------------------------------
// Views — what a route RETURNS.
//
// Width-bounded strings, no format checks, no transforms — see the note above
// the input primitives, and `./user`'s longer version of the same argument.
// `colour` is the exception and stays an enum; its field docstring says why.
// ---------------------------------------------------------------------------

/**
 * A label as the board's set holds it — `GET /boards/:id/labels`,
 * `POST /boards/:id/labels`, `PATCH /labels/:id/name` and
 * `PATCH /labels/:id/colour`. (`DELETE /labels/:id` answers `204` with no
 * body, so it needs no shape — `DELETE /lists/:id`'s precedent.)
 *
 * One view for all four, rather than a narrower shape per route, because they
 * all answer the same question — *what is this label now* — and a client that
 * has just recoloured one needs the same fields as one that has just listed
 * them. `listSchema`'s argument, one table over.
 *
 * ⚠ **It is NOT `cardLabelSchema` and neither is a subset the other should be
 * derived from by hand.** This shape is the *set's* member; that one is what
 * rides on a card. See `cardLabelSchema`.
 */
export const labelSchema = z.object({
  id: ulidSchema,
  /**
   * `labels.board_id char(26) NOT NULL`, `fk_labels_board_id` referencing
   * `boards(id)` **ON DELETE CASCADE**.
   *
   * Never null — the column enforces it — and on the wire because
   * `FR-CARD-010`'s "Labels are board-scoped" is the fact a client needs to
   * know which board's picker it is holding. It is also what makes
   * `boardContentsSchema.labels` self-describing when a client caches sets
   * across boards.
   *
   * ⚠ The cascade is not a detail: deleting a board takes its labels with it,
   * which is why `labels` needs no `deleted_at` of its own and why there is no
   * `isLiveLabel()` anywhere in the server. `apps/server/src/labels/
   * permissions.ts`' header carries the long form.
   */
  boardId: ulidSchema,
  /**
   * `labels.name varchar(255) NULL` — `null` for an unnamed label, which is
   * the state all six seeded labels start in and a state any label can be
   * returned to (`PATCH /labels/:id/name` with `null`).
   *
   * ⚠ **Width-bounded only: no `.trim()`, no `.min(1)`.** The input primitive
   * trims and refuses empty; this one describes a *row* and must not refuse
   * one a fixture, a seed or an import path legitimately wrote. That is the
   * input/view split this package is arranged around and
   * `cardDetailSchema.description` is its sharpest instance; this is the same
   * call one field over, with the extra note that the column width makes the
   * bound here a mirror of the database rather than of a product rule.
   *
   * `null` is never `''`. The boundary makes sure of it, so a client renders
   * "unnamed" on exactly one test.
   */
  name: z.string().max(LABEL_NAME_MAX_LENGTH).nullable(),
  /**
   * `labels.colour varchar(32) NOT NULL`, `ck_labels_colour`.
   *
   * ⚠ **The enum on the view too, not a bounded string.** That is
   * `boardSchema.visibility`'s precedent rather than `publicProfileSchema.bio`'s,
   * and the distinction is whether an out-of-vocabulary value is *stale data a
   * reader should tolerate* or *inexpressible in the column*. A bio outside its
   * cap can exist (the column is wider than the product rule); a colour outside
   * this palette cannot, because the CHECK refuses it at INSERT. So a value
   * here that fails to parse means the constraint and this list have drifted,
   * which is something a read should surface rather than pass through.
   */
  colour: labelColourSchema,
  /**
   * `labels.position varchar(64) COLLATE "C" NOT NULL` — the fractional rank
   * that fixes **set order**, which is the order `FR-CARD-009` AC2 names: "all
   * three render on the card front **in the board's label set order**".
   *
   * On the wire, and `listSchema.position` is the precedent. The client needs
   * it to place an optimistically-created label in the set before the server
   * answers, and to sort a card's labels against the set it already holds.
   *
   * ⚠ **A client never computes one** (SC-2). No route in this block accepts a
   * rank, `createLabelBodySchema` rejects a `position` key outright, and B15
   * only ever appends — the server reads the board's last rank inside the
   * transaction and calls `rankBetween(last, null)`. Read it, sort by it, never
   * mint it.
   */
  position: z.string().max(RANK_MAX_LENGTH),
  /**
   * `labels.created_at timestamptz(3) NOT NULL DEFAULT now()`.
   *
   * A bounded string rather than `z.iso.datetime()` or a coerced `Date`, for
   * the reason `listSchema` and every other timestamp in this package state:
   * `pg` hands a `timestamptz` back in Postgres' own spelling
   * (`2026-08-12 12:00:00.123+00`), which is a legitimate instant and not an
   * ISO 8601 `T`/`Z` form, so pinning the serialisation here would make this
   * schema reject the value the database actually produced.
   */
  createdAt: z.string().min(1),
  /**
   * `labels.updated_at`, owned by `trg_labels_set_updated_at`.
   *
   * Carried because it is the only evidence over HTTP that a rename or a
   * recolour landed — `listSchema`'s call, and the same caveat applies: no
   * requirement in `FR-CARD-009` or `FR-CARD-010` asks for it, so its presence
   * is not a precedent for adding more.
   *
   * ⚠ **Nothing in the application ever writes this column**, and the trigger
   * fires `WHEN (OLD.* IS DISTINCT FROM NEW.*)` — so renaming a label to the
   * name it already has leaves this value **byte-identical**. That is correct
   * and must not be "fixed": an application `set` that also wrote it would be a
   * second writer of a trigger-derived value (the `users.email_verified_at`
   * mistake this repository names three times) and would make a no-change save
   * look like a change to every client watching the field.
   */
  updatedAt: z.string().min(1),
});

export type Label = z.infer<typeof labelSchema>;

/**
 * `GET /boards/:id/labels` — the board's whole set, in set order
 * (`ORDER BY position, id`).
 *
 * A **bare array**, mirroring `listListSchema` at `GET /boards/:id/lists`, not
 * `{ labels: [...] }`. And `[]` is a legitimate, expected value that can only
 * ever mean "this board has no labels": boards created before B15 are not
 * backfilled (see `DEFAULT_BOARD_LABEL_COLOURS`), so an empty set is the state
 * of every board that already exists, and a board whose six labels were all
 * deleted is indistinguishable from one of those. It can **never** mean "you
 * may not see this board" — that is a 404, decided by a separate statement
 * before this array is read at all.
 */
export const labelListSchema = z.array(labelSchema);

export type LabelList = z.infer<typeof labelListSchema>;

/**
 * One label as a **card** carries it — `boardFaceCardSchema.labels` in
 * `./board-contents` and `cardDetailSchema.labels` in `./card-detail`.
 *
 * Three fields: which label, what it is called, what colour it is. That is
 * everything a card front's bar and a card back's chip render, and it is
 * emphatically **not** `labelSchema`.
 *
 * ---------------------------------------------------------------------------
 * ⚠ WHY IT IS THREE FIELDS AND MUST NOT GROW TOWARDS `labelSchema`
 * ---------------------------------------------------------------------------
 * The precedent is one block old: `cardListRefSchema` in `./card-detail` is
 * `{ id, title, archived }` rather than `listSchema`, "because nesting the
 * whole list view here would put all four on every card open". **The argument
 * is far stronger here**, because this shape rides on the board face — the one
 * response in the product that scales with the size of the board. Two hundred
 * cards carrying up to a dozen labels each is 2,400 of these objects in one
 * payload, and `00-`'s third cross-cutting principle (the board stays legible
 * at a glance) is the same principle on the wire.
 *
 * What is left out and where a client gets it instead:
 *
 *   - `boardId` — recoverable from the card, which carries its own.
 *   - `position` — recoverable from `boardContentsSchema.labels`, the board's
 *     whole set, which is on the same payload and exists partly for this.
 *   - `createdAt` / `updatedAt` — noise on a badge.
 *
 * ⚠ **The array's ORDER is the board's set order, not application order.**
 * `FR-CARD-009` AC2 verbatim: "given a card carries three labels, when the
 * board is viewed, then all three render on the card front **in the board's
 * label set order**". So the join is `card_labels → labels` with
 * `ORDER BY labels.position, labels.id` — never `card_labels.created_at`,
 * which passes every "the three labels are there" assertion while violating
 * the only sentence that says what order means here. Both surfaces must order
 * identically or the card front and the card back disagree about one card with
 * nothing failing.
 *
 * ⚠ **Colourless labels are present.** `FR-CARD-009` says they "render only on
 * the card back"; that is `FE-15.2`'s suppression, not a server filter. See
 * `LABEL_COLOURS`.
 */
export const cardLabelSchema = z.object({
  id: ulidSchema,
  /** `labels.name`, `null` when unnamed — see `labelSchema.name`. */
  name: z.string().max(LABEL_NAME_MAX_LENGTH).nullable(),
  /** `labels.colour` — see `labelSchema.colour`. The enum, on the view too. */
  colour: labelColourSchema,
});

export type CardLabel = z.infer<typeof cardLabelSchema>;
